import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import { hotComics, latestUpdates, bannerComics, categories } from '@/mocks/comics';

const BANNER_INTERVAL = 5000;

export default function Home() {
  const [bannerIndex, setBannerIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback((idx: number) => {
    setBannerIndex((idx + bannerComics.length) % bannerComics.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setBannerIndex(prev => (prev + 1) % bannerComics.length);
    }, BANNER_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused]);

  const current = bannerComics[bannerIndex];

  return (
    <>
      <HelmetContent />

      {/* Hero Banner */}
      <section
        className="relative w-full h-[420px] md:h-[560px] overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {bannerComics.map((comic, idx) => (
          <div
            key={comic.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              idx === bannerIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img
              src={comic.coverImage}
              alt={comic.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 z-20" />

        <div className="absolute inset-0 z-30 flex items-center">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-xl">
                <div className="flex items-center gap-2 mb-4">
                  <span className="bg-primary-500 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
                    热门推荐
                  </span>
                  <span className="text-white/80 text-sm">{current.author}</span>
                </div>
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  {current.title}
                </h2>
                <p className="text-white/80 text-sm md:text-base leading-relaxed mb-6 line-clamp-2 max-w-lg">
                  {current.description}
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/comic/${current.id}`}
                    className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                  <Link
                    to={`/category/${current.category}`}
                    className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多{getCategoryName(current.category)}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
          {bannerComics.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setBannerIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                idx === bannerIndex
                  ? 'bg-white w-6'
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`展示第${idx + 1}张`}
            />
          ))}
        </div>

        {/* Arrows */}
        <button
          onClick={() => goTo(bannerIndex - 1)}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
          aria-label="上一个"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-left-s-line text-xl"></i>
          </span>
        </button>
        <button
          onClick={() => goTo(bannerIndex + 1)}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
          aria-label="下一个"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-xl"></i>
          </span>
        </button>
      </section>

      {/* Category Navigation */}
      <section className="py-8 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
            {categories.map(cat => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-background-200/70 bg-white text-foreground-700 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Comics */}
      <section className="py-8 md:py-12 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                最受读者欢迎的韩漫作品
              </p>
            </div>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="false">
            {hotComics.slice(0, 10).map(comic => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-10 md:py-14 bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { num: '5000+', label: '漫画作品' },
              { num: '200万+', label: '注册用户' },
              { num: '每日更新', label: '更新频率' },
              { num: '4.8分', label: '用户好评' },
            ].map(stat => (
              <div key={stat.label}>
                <p className="font-heading text-2xl md:text-3xl font-bold text-white">
                  {stat.num}
                </p>
                <p className="text-white/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-8 md:py-12 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                最近更新
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                新鲜出炉的韩漫章节
              </p>
            </div>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="false">
            {latestUpdates.map(comic => (
              <ComicCard key={comic.id} comic={comic} size="small" />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              为什么选择韩漫网？
            </h2>
            <p className="text-sm text-foreground-500 mt-2">
              致力于打造最好的韩漫阅读体验
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: 'ri-shield-check-line', title: '正版授权', desc: '收录海量正版韩漫作品，画质高清无水印' },
              { icon: 'ri-download-cloud-2-line', title: '免费阅读', desc: '全部漫画免费在线阅读，无需付费订阅' },
              { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '流畅的下拉式阅读模式，滚动即可翻页' },
              { icon: 'ri-timer-flash-line', title: '更新及时', desc: '同步韩国最新连载，每日准时更新' },
            ].map(feat => (
              <div key={feat.title} className="text-center p-5 rounded-lg bg-white border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 bg-primary-50 rounded-full flex items-center justify-center">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className={`${feat.icon} text-xl text-primary-500`}></i>
                  </span>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                  {feat.title}
                </h3>
                <p className="text-sm text-foreground-600">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              常见问题
            </h2>
          </div>
          <div className="space-y-3">
            {[
              { q: '韩漫网是否需要付费？', a: '韩漫网所有漫画完全免费在线阅读，无需任何付费订阅，注册账号即可畅读全部作品。' },
              { q: '漫画更新频率如何？', a: '我们与韩国各大漫画平台保持同步更新，热门连载作品每日更新，确保读者第一时间看到最新内容。' },
              { q: '支持什么设备阅读？', a: '韩漫网采用响应式设计，支持电脑、平板、手机等多种设备，在任何屏幕上都能享受流畅的阅读体验。' },
              { q: '什么是下拉式阅读模式？', a: '下拉式阅读是专为手机端优化的阅读方式，只需向下滑动即可连续阅读，无需点击翻页按钮，让阅读体验更加流畅自然。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-white rounded-lg border border-background-200/70 overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 select-none">
                  {faq.q}
                  <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-3xl mx-auto text-center px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
            开启你的韩漫之旅
          </h2>
          <p className="text-sm text-foreground-600 mb-6">
            数千部韩漫作品等你来发现，现在就加入韩漫网，畅享免费阅读！
          </p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 bg-primary-500 text-white px-8 py-3.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            立即浏览漫画库
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </Link>
        </div>
      </section>

      {/* JSON-LD for Homepage */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getHomeSchema()) }} />
    </>
  );
}

function getCategoryName(slug: string): string {
  const cat = categories.find(c => c.slug === slug);
  return cat ? cat.name : '全部';
}

function HelmetContent() {
  return null;
}

function getHomeSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩漫网 - 韩漫免费在线阅读平台首页',
    description: '韩漫网首页，汇集热门韩国漫画推荐、最新更新、分类导航，免费在线阅读无删减韩漫。',
    url: 'https://englishwhisky.cn',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://englishwhisky.cn',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://englishwhisky.cn',
        },
      ],
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: hotComics.map((comic, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'ComicSeries',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://englishwhisky.cn/comic/${comic.id}`,
        },
      })),
    },
  };
}